import React, { useState } from 'react';
import { useCourses } from '../../../shared/hooks/useCourses';
import { CoursesProps } from '../../../interfaces/screens/content/Courses';
import CourseItem from '../../../components/CourseItem';
import { CourseItem as CourseItemType } from '../../../interfaces/api/courses';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CourseEnrollmentModal from '../courseEnrollement';

/**
 * Courses component displays a list of courses with infinite scroll and refresh capabilities
 *
 * @component
 */
const Courses: React.FC<CoursesProps> = () => {
  const { t } = useTranslation();
  const { coursesList, loading, refreshData, error } = useCourses();
  const [selectedCourse, setSelectedCourse] = useState<CourseItemType | null>(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const onCoursePress = (course: CourseItemType) => {
    if (course.enrolled) {
      // Navigate to course details if already enrolled
      navigate(`/courses/${course.name.split(' ').join('-')}/${course.id}`, {
        state: { courseName: course.name },
      });
    } else {
      // Show enrollment modal if not enrolled
      setSelectedCourse(course);
      setShowEnrollmentModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowEnrollmentModal(false);
    setSelectedCourse(null);
  };

  const handleEnrollmentSuccess = () => {
    handleCloseModal();
    refreshData();
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <p className="text-red-500 dark:text-red-400 mb-3">{t('courses.errorLoading')}</p>
        <button
          onClick={refreshData}
          className="px-4 py-2 bg-primary dark:bg-blue-600 text-white rounded-full hover:bg-primary/90 dark:hover:bg-blue-700 transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto pt-4">
      {loading && coursesList.length === 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="rounded-xl bg-gray-200 dark:bg-gray-700 aspect-video w-full mb-4" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-3/4 mb-3" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full w-full" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
            {coursesList.map((course) => (
              <CourseItem
                key={`course-${course.id}`}
                title={course.name}
                description={course.brief}
                duration={course.duration}
                cover={course.cover.url}
                onPress={() => onCoursePress(course)}
              />
            ))}
          </div>
          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-blue-400" />
            </div>
          )}
        </>
      )}

      {/* Course Enrollment Modal */}
      {showEnrollmentModal && selectedCourse && (
        <CourseEnrollmentModal
          courseId={selectedCourse.id}
          onClose={handleCloseModal}
          onEnrollmentSuccess={handleEnrollmentSuccess}
        />
      )}
    </div>
  );
};

export default Courses;
